import {
  ErrorDisplay,
  Feedback,
  FormCheckbox,
  FormHub,
  FormNumberField,
  FormTextField,
  SubmitButton,
  useFormEvents,
} from '@lib/common';
import { CorporateAccount, FormStatus } from '@lib/state';
import { Box, Grid } from '@material-ui/core';
import { CorporateAccountForms, corporateAccountService } from 'app/state';
import React, { useCallback, useMemo } from 'react';

interface Props {
  corporateAccount: CorporateAccount;
}

interface InvoiceSettingsFormFields {
  invoiceRecipients?: string;
  doNotSendInvoice?: boolean;
  rateThresholdPercentage?: number;
  poField?: string;
}

export function CorporateAccountInvoiceSettingsForm({ corporateAccount }: Props) {
  const defaultValues: InvoiceSettingsFormFields = useMemo(
    () => ({
      invoiceRecipients: corporateAccount?.invoiceRecipients ?? '',
      doNotSendInvoice: corporateAccount?.doNotSendInvoice ?? false,
      poField: corporateAccount?.poField ?? '',
    }),
    [corporateAccount]
  );

  const [formEvents, resetFormUI] = useFormEvents(CorporateAccountForms.UpdateInvoiceSettings);

  const onSubmit = useCallback(
    (values: InvoiceSettingsFormFields) => {
      const rateThresholdPercentage = values.rateThresholdPercentage
        ? +values.rateThresholdPercentage
        : undefined;
      corporateAccountService.updateInvoiceSettings(corporateAccount.id, {
        ...values,
        rateThresholdPercentage,
      });
    },
    [corporateAccount]
  );

  return (
    <FormHub onSubmit={onSubmit} formOptions={{ defaultValues }}>
      <Grid container spacing={1}>
        <Grid item md={6} xs={12}>
          <FormTextField
            name="invoiceRecipients"
            label="Invoice Recipients"
            defaultValue={corporateAccount.invoiceRecipients}
            fullWidth
            data-testid="corporateAccountInvoiceRecipientsInput"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormTextField
            name="poField"
            label="PO Field"
            defaultValue={corporateAccount.poField}
            fullWidth
            data-testid="corporateAccountPOFieldInput"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormNumberField
            name="rateThresholdPercentage"
            label="Nightly Rate Threshold"
            defaultValue={corporateAccount.rateThresholdPercentage}
            InputProps={{ endAdornment: '%' }}
            validationOptions={{ min: 0, max: 100 }}
            data-testid="corporateAccountRateThresholdPercentageInput"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormCheckbox
            name="doNotSendInvoice"
            label="Do Not Send Invoice"
            defaultChecked={corporateAccount.doNotSendInvoice}
            fullWidth
            data-testid="corporateAccountDoNotSendInvoiceCheckbox"
          />
        </Grid>
      </Grid>
      <Box py={1}>
        <Feedback
          show={formEvents.status === FormStatus.Success}
          severity="success"
          onHide={resetFormUI}
          data-testid="invoiceGroupingSuccessDisplay"
        >
          Invoice Settings saved successfully!
        </Feedback>
        <ErrorDisplay error={formEvents.error} />
      </Box>
      <Box display="flex" justifyContent="flex-end" pb={2}>
        <SubmitButton
          name="saveInvoiceSettingsButton"
          variant="contained"
          color="secondary"
          pending={formEvents.status === FormStatus.Pending}
          disabled={formEvents.status === FormStatus.Pending}
          data-testid="corporateAccountSaveInvoiceSettingsButton"
        >
          Save Invoice Settings
        </SubmitButton>
      </Box>
    </FormHub>
  );
}
